/**
 * PlanMapLockFixturePage — a dev-only harness for the onboarding plan's locked top-down map
 * (`spec-onboarding-3d-plan-map-lock`, R7).
 *
 * ## Why a fixture page
 *
 * Product screens mount `PlanMapHost` now: onboarding's stage (Position on map), the Floor Plan,
 * Camera Feeds and Observers editors' maps, the read-only plan, and the plan and opening-view
 * pictures. The page is still the lock's rig because it isolates the lock: no twin, no sign-in and
 * no saved data, just an invented plan — five boxes on the ground and one on an upper floor — with
 * controls to enter and leave the plan view, reload the map style and turn the map, so a real
 * browser can drive it at measured pitches (`tests/twin/planMapLock.pw.spec.ts`) and the
 * before/after captures are taken on a fixed rig.
 *
 * The lock's free camera and full poses (`spec-onboarding-persistent-scene-lock-poses`, R7): _Free
 * camera_ asks for the free state, where heights follow the tilt; _Leave to pose_ and _Enter from
 * pose_ leave and lock in one move to fixed invented poses near the boxes.
 *
 * It opens with the plan view OFF and the camera tilted to 60°, so "Enter plan view" has something
 * to flatten and "Leave plan view" returns there. `?start=plan` opens it locked instead, which is
 * the host's mount path.
 *
 * Nothing here is real data and nothing is written anywhere; the only network traffic is the
 * basemap. As with `/plan-camera-fixture`, the route exists only outside production and the
 * fixture's constants live inside the component, so the bundle carries none of them.
 *
 * `window.__planMapLock` is the rig's driver: the view's state and height scale, the lock's
 * recorded settings, the camera, each box's projected top and base in client pixels through the
 * matrix react-three-map renders with, and the canvas's machine look and shader-program count
 * (`spec-onboarding-persistent-scene-model-look`). `lock.free(on)`, `lock.leaveToPose()` and
 * `lock.enterFromPose()` press the free-camera and pose controls; `lock.poses` holds the two poses.
 *
 * It also hosts the post-translucency rig (`spec-onboarding-3d-plan-post-translucency`,
 * `tests/twin/postTranslucency.pw.spec.ts`): three see-through layers drawn as the plan draws its
 * own (`transparent`, depth writes off, an explicit `renderOrder`) — a flat fill at 0.15, a lit
 * wall-like box at 0.5 and a black-and-white textured plane at 0.4 standing in for a PDF — hidden
 * until the rig shows one, and a switch for the live twin's post-processing.
 * `window.__planMapLock.translucency` drives them: `show(name | 'all')`, `hide()`,
 * `opaque(on)`, `post(on | switches)`, `layers()` (each shown layer's projected rectangle) and
 * `composerMounted()`. `extras(on)` adds two opaque props for the effects' own checks: an over-bright
 * box that Bloom must make glow, and a box standing on a slab whose inside corner N8AO must shade.
 *
 * `?maps=2` adds a second plan map below the first that stays mounted, a "Remount map" control for
 * the first, and draws both maps' boxes with the shared `MACHINE_MATERIAL` and one shared geometry,
 * as two plan maps on one page would share them (`spec-onboarding-3d-plan-plan-host-fixes`,
 * criterion 12 and R4.2: a closed map releases what it drew while the other still draws it). The
 * second map's driver is `window.__planMapLock2`. Without the parameter the page is unchanged.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Box3,
  BoxGeometry,
  DataTexture,
  Matrix4,
  NearestFilter,
  RGBAFormat,
  SRGBColorSpace,
  Vector3,
} from 'three';
import type { Mesh, Object3D } from 'three';
import { useThree } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import { useMap } from 'react-three-map';
import { Button, Select } from '@repo/design-system';
import { MACHINE_MATERIAL } from '@repo/ui';
import { accent, semantic, space, support, typeRole } from '@repo/design-tokens';
import Config from '../../common/Config';
import { PlanMapHost } from '../../components/3D/planView/PlanMapHost';
import { PlanHeightGroup, usePlanView } from '../../components/3D/planView/PlanHeightScale';
import { useMachineLook } from '../../components/3D/planView/MachineLook';
import { readPlanLockSettings } from '../../components/3D/planView/planMapLock';
import type { PlanLockMap, PlanPose } from '../../components/3D/planView/planMapLock';
import type { PostProcessSwitches } from '@repo/ui';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${semantic.surface.app};
`;
const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[3]} ${space[4]};
`;
// The Select fills its container; keep it to a control's width so the row stays on one line.
const BearingSlot = styled.div`
  width: calc(${space[12]} * 3);
`;
const Status = styled.p`
  margin: 0 0 0 ${space[3]};
  color: ${semantic.text.secondary};
  font-size: ${typeRole.caption1.fontSize};
`;
const MapRegion = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
`;
// The second map of `?maps=2`, under the first.
const SecondMapRegion = styled.div`
  flex: 0 0 40%;
  min-height: 0;
  position: relative;
  border-top: 1px solid ${semantic.border.default};
`;

type Box = { id: string; x: number; z: number; baseY: number };
type LayerName = 'fill' | 'wall' | 'pdf';
type Layer = { name: LayerName; opacity: number; x: number; z: number; w: number; d: number };
type Translucency = {
  shown: LayerName[];
  opaque: boolean;
  post: boolean | PostProcessSwitches;
  extras: boolean;
};
const LAYER_NAMES: LayerName[] = ['fill', 'wall', 'pdf'];
const warnRig = (message: string) => console.warn(`[planMapLock fixture] ${message}`);

const BOX_SIZE = 1;
const BOX_HEIGHT = 2.45;

/** Projects with the matrix react-three-map renders with, as the camera spike did. */
const Driver = ({
  boxes,
  onState,
  tiltOnStart,
  translucency,
  name = '__planMapLock',
  lock,
}: {
  boxes: Box[];
  onState: (state: string) => void;
  tiltOnStart: boolean;
  /** The rig's see-through driver; only the first map has one. */
  translucency?: Record<string, unknown>;
  /** The window property the driver is published on. */
  name?: string;
  /** The rig's free-camera and pose driver; only the first map has one. */
  lock?: Record<string, unknown>;
}) => {
  const map = useMap() as any;
  const get = useThree(s => s.get);
  const { getHeightScale, getState } = usePlanView();
  const machineLook = useMachineLook();

  useEffect(() => {
    if (tiltOnStart && getState() === 'off') map.setPitch(60);
    // Only on first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let styleLoads = 0;
    const report = () => window.setTimeout(() => onState(getState()), 0);
    const countStyle = () => {
      styleLoads += 1;
    };
    map.on('moveend', report);
    map.on('style.load', countStyle);
    report();

    const pv = new Matrix4();
    const v = new Vector3();
    const toClient = (p: Vector3): [number, number] => {
      pv.fromArray(get().camera.userData.projByView);
      v.copy(p).applyMatrix4(pv);
      const r = map.getCanvas().getBoundingClientRect();
      return [r.left + ((v.x + 1) / 2) * r.width, r.top + ((1 - v.y) / 2) * r.height];
    };
    const meshes = () => {
      const found: Record<string, Object3D> = {};
      get().scene.traverse(o => {
        if ((o as Mesh).isMesh && typeof o.userData.boxId === 'string') found[o.userData.boxId] = o;
      });
      return found;
    };
    const measure = () => {
      const all = meshes();
      const box = new Box3();
      return boxes.map(b => {
        const o = all[b.id];
        if (!o) return { id: b.id, missing: true };
        box.setFromObject(o);
        const c = box.getCenter(new Vector3());
        const top = toClient(new Vector3(c.x, box.max.y, c.z));
        const base = toClient(new Vector3(c.x, box.min.y, c.z));
        const corners: [number, number][] = [];
        [box.min.x, box.max.x].forEach(x =>
          [box.min.y, box.max.y].forEach(y =>
            [box.min.z, box.max.z].forEach(z => corners.push(toClient(new Vector3(x, y, z)))),
          ),
        );
        const xs = corners.map(p => p[0]);
        const ys = corners.map(p => p[1]);
        return {
          id: b.id,
          top,
          base,
          lean: Math.hypot(top[0] - base[0], top[1] - base[1]),
          baseWorldY: box.min.y,
          heightWorld: box.max.y - box.min.y,
          rect: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)],
        };
      });
    };
    const projection = () => map.getCamera()?.['camera-projection'] ?? 'perspective';
    /** Each shown see-through layer: its top face's projected corners and their bounding rect. */
    const layers = () => {
      const out: unknown[] = [];
      const box = new Box3();
      get().scene.traverse(o => {
        const name = o.userData.layerName;
        if (!(o as Mesh).isMesh || typeof name !== 'string' || !o.visible) return;
        let shown = true;
        o.traverseAncestors(p => {
          if (!p.visible) shown = false;
        });
        if (!shown) return;
        box.setFromObject(o);
        const outline = [
          [box.min.x, box.min.z],
          [box.max.x, box.min.z],
          [box.max.x, box.max.z],
          [box.min.x, box.max.z],
        ].map(([x, z]) => toClient(new Vector3(x, box.max.y, z)));
        const xs = outline.map(p => p[0]);
        const ys = outline.map(p => p[1]);
        out.push({
          name,
          opacity: o.userData.layerOpacity,
          outline,
          rect: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)],
        });
      });
      return out;
    };

    (window as any)[name] = {
      map,
      state: getState,
      scale: getHeightScale,
      settings: () => readPlanLockSettings(map as PlanLockMap),
      camera: () => ({
        projection: projection(),
        pitch: map.getPitch(),
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        center: map.getCenter().toArray(),
        maxZoom: map.getMaxZoom(),
      }),
      boxes: measure,
      translucency: translucency && {
        ...translucency,
        layers,
        // The composer renders from a useFrame of priority 1, taking over R3F's own render.
        composerMounted: () => get().internal.priority > 0,
      },
      lock,
      styleLoads: () => styleLoads,
      /** The canvas's machine look (`spec-onboarding-persistent-scene-model-look`, criterion 12). */
      look: () => machineLook?.getLook() ?? null,
      /** Shader programs compiled: unchanged across a move means none was rebuilt mid-move. */
      programs: () => get().gl.info.programs?.length ?? 0,
      setStyle: (url: string) => map.setStyle(url),
      /** Every animation frame for `ms`: pitch, projection, scale and every box. */
      sample: (ms: number) =>
        new Promise(resolve => {
          const rows: unknown[] = [];
          const t0 = performance.now();
          const tick = (now: number) => {
            rows.push({
              t: Math.round(now - t0),
              t0abs: now,
              state: getState(),
              pitch: map.getPitch(),
              center: map.getCenter().toArray(),
              zoom: map.getZoom(),
              bearing: map.getBearing(),
              projection: projection(),
              scale: getHeightScale(),
              boxes: measure(),
            });
            if (now - t0 < ms) requestAnimationFrame(tick);
            else resolve(rows);
          };
          requestAnimationFrame(tick);
        }),
    };
    return () => {
      map.off('moveend', report);
      map.off('style.load', countStyle);
      delete (window as any)[name];
    };
  }, [map, get, getHeightScale, getState, boxes, onState, translucency, name, lock, machineLook]);

  return null;
};

/** A black-and-white texel grid, as a PDF's line drawing is: its texels are near black or white. */
const makePdfTexture = () => {
  const n = 8;
  const data = new Uint8Array(n * n * 4);
  for (let i = 0; i < n * n; i++) {
    const v = ((i % n) + Math.floor(i / n)) % 2 === 0 ? 255 : 0;
    data.set([v, v, v, 255], i * 4);
  }
  const texture = new DataTexture(data, n, n, RGBAFormat);
  texture.colorSpace = SRGBColorSpace;
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;
  texture.needsUpdate = true;
  return texture;
};

/** The see-through layers, drawn with the plan's mechanism: transparent, no depth writes, ordered. */
const TranslucentLayers = ({ layers, state }: { layers: Layer[]; state: Translucency }) => {
  const [pdfTexture] = useState(makePdfTexture);
  useEffect(() => () => pdfTexture.dispose(), [pdfTexture]);
  return (
    <>
      {layers.map(l => {
        const visible = state.shown.includes(l.name);
        const opacity = state.opaque ? 1 : l.opacity;
        const userData = { layerName: l.name, layerOpacity: l.opacity };
        if (l.name === 'wall') {
          return (
            <mesh
              key={l.name}
              visible={visible}
              position={[l.x, BOX_HEIGHT / 2, l.z]}
              renderOrder={30}
              userData={userData}
            >
              <boxGeometry args={[l.w, BOX_HEIGHT, l.d]} />
              <meshStandardMaterial
                color={accent.violet}
                transparent
                opacity={opacity}
                depthWrite={false}
              />
            </mesh>
          );
        }
        return (
          <mesh
            key={l.name}
            visible={visible}
            position={[l.x, 0.01, l.z]}
            rotation={[-Math.PI / 2, 0, 0]}
            renderOrder={l.name === 'pdf' ? 10 : 40}
            userData={userData}
          >
            <planeGeometry args={[l.w, l.d]} />
            {l.name === 'pdf' ? (
              <meshBasicMaterial
                map={pdfTexture}
                transparent
                opacity={opacity}
                depthWrite={false}
              />
            ) : (
              <meshBasicMaterial
                color={support.mint}
                transparent
                opacity={opacity}
                depthWrite={false}
              />
            )}
          </mesh>
        );
      })}
    </>
  );
};

/** Opaque props for the effects' own checks, hidden unless the rig asks for them. */
const EffectProps = ({ visible }: { visible: boolean }) => (
  <group visible={visible}>
    {/* Emissive above Bloom's luminance threshold of 1, so it must glow. */}
    <mesh position={[-4.5, 0.5, 1.5]} userData={{ layerName: 'glow', layerOpacity: 1 }}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color={accent.butter} emissive={accent.butter} emissiveIntensity={4} />
    </mesh>
    {/* A box on a slab: from above, the slab around the box's foot is an inside corner. */}
    <mesh position={[4.5, 0.1, 1.5]} userData={{ layerName: 'aoSlab', layerOpacity: 1 }}>
      <boxGeometry args={[3, 0.2, 3]} />
      <meshStandardMaterial color={support.lavender} />
    </mesh>
    <mesh position={[4.5, 0.7, 1.5]} userData={{ layerName: 'aoBox', layerOpacity: 1 }}>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color={support.lavender} />
    </mesh>
  </group>
);

const PlanMapLockFixturePage = () => {
  // Fixture constants live inside the component so a production bundle carries none of them.
  const origin = { latitude: 51.5, longitude: -0.12 };
  const [boxes] = useState<Box[]>(() => [
    { id: 'G-NW', x: -7, z: -4, baseY: 0 },
    { id: 'G-NE', x: 7, z: -4, baseY: 0 },
    { id: 'G-C', x: 0, z: 0, baseY: 0 },
    { id: 'G-SW', x: -7, z: 4, baseY: 0 },
    { id: 'G-SE', x: 7, z: 4, baseY: 0 },
    { id: 'U-1', x: 3.5, z: 0, baseY: 3.5 },
  ]);
  const alternateStyle = 'mapbox://styles/mapbox/light-v11';
  // Clear of the boxes (x ±7, z ±4 and the centre), on the ground floor.
  const [layers] = useState<Layer[]>(() => [
    { name: 'fill', opacity: 0.15, x: -3.5, z: -4, w: 4, d: 2.5 },
    { name: 'wall', opacity: 0.5, x: 3.5, z: -4, w: 3, d: 0.8 },
    { name: 'pdf', opacity: 0.4, x: 0, z: 2.5, w: 4, d: 2.5 },
  ]);
  const [translucency, setTranslucency] = useState<Translucency>({
    shown: [],
    opaque: false,
    post: true,
    extras: false,
  });
  const translucencyRef = useRef(translucency);
  translucencyRef.current = translucency;
  const translucencyApi = useMemo(
    () => ({
      show: (name?: string) => {
        if (name === 'all') setTranslucency(t => ({ ...t, shown: [...LAYER_NAMES] }));
        else if (name && (LAYER_NAMES as string[]).includes(name))
          setTranslucency(t => ({ ...t, shown: [name as LayerName] }));
        else warnRig(`show: unknown layer "${String(name)}"`);
      },
      hide: () => setTranslucency(t => ({ ...t, shown: [] })),
      opaque: (on?: boolean) => {
        if (typeof on !== 'boolean') warnRig('opaque: expected true or false');
        else setTranslucency(t => ({ ...t, opaque: on }));
      },
      post: (on?: boolean | PostProcessSwitches) => {
        if (typeof on === 'boolean' || (on && typeof on === 'object'))
          setTranslucency(t => ({ ...t, post: on }));
        else warnRig('post: expected true, false or a set of switches');
      },
      extras: (on?: boolean) => {
        if (typeof on !== 'boolean') warnRig('extras: expected true or false');
        else setTranslucency(t => ({ ...t, extras: on }));
      },
      state: () => translucencyRef.current,
    }),
    [],
  );

  const search = new URLSearchParams(window.location.search);
  const startLocked = search.get('start') === 'plan';
  const twoMaps = search.get('maps') === '2';
  // `?maps=2`: one geometry for every box on both maps, drawn with the shared machine material.
  const [sharedGeometry] = useState(() =>
    twoMaps ? new BoxGeometry(BOX_SIZE, BOX_HEIGHT, BOX_SIZE) : null,
  );
  const [firstMapKey, setFirstMapKey] = useState(0);
  const noState = useCallback(() => {}, []);
  const [planView, setPlanView] = useState(startLocked);
  // R7: the free camera and the two invented poses, east and west of the boxes.
  const [free, setFree] = useState(false);
  const [leavePose, setLeavePose] = useState<PlanPose | null>(null);
  const [enterPose, setEnterPose] = useState<PlanPose | null>(null);
  const poses = useMemo(() => {
    const metre = 1 / (111_320 * Math.cos((origin.latitude * Math.PI) / 180));
    return {
      leave: {
        center: { lng: origin.longitude + 30 * metre, lat: origin.latitude },
        zoom: 20,
        bearing: 40,
        pitch: 50,
      },
      enter: {
        center: { lng: origin.longitude - 10 * metre, lat: origin.latitude },
        zoom: 22.5,
        bearing: -20,
      },
    };
    // The origin is a constant of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const leaveToPose = useCallback(() => {
    setLeavePose(poses.leave);
    setPlanView(false);
  }, [poses]);
  const enterFromPose = useCallback(() => {
    setEnterPose(poses.enter);
    setPlanView(true);
  }, [poses]);
  const lockApi = useMemo(
    () => ({
      free: (on?: boolean) => {
        if (typeof on !== 'boolean') warnRig('free: expected true or false');
        else setFree(on);
      },
      leaveToPose,
      enterFromPose,
      poses,
    }),
    [leaveToPose, enterFromPose, poses],
  );
  const [mapStyle, setMapStyle] = useState(Config.mapboxStyleUrl);
  const [bearing, setBearing] = useState('0');
  const [announcement, setAnnouncement] = useState('');
  const lastState = useRef<string | null>(null);

  const onState = useCallback((state: string) => {
    if (state === lastState.current) return;
    lastState.current = state;
    if (state === 'locked') setAnnouncement('Plan view on');
    else if (state === 'off') setAnnouncement('Plan view off');
    else if (state === 'free') setAnnouncement('Free camera');
  }, []);

  const select = (id: string) => (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    setAnnouncement(`Selected: ${id}`);
  };

  const turn = (value: string) => {
    setBearing(value);
    (window as any).__planMapLock?.map.setBearing(Number(value));
  };

  const ground = boxes.filter(b => b.baseY === 0);
  const upper = boxes.filter(b => b.baseY !== 0);
  const draw = (b: Box) =>
    sharedGeometry ? (
      <mesh
        key={b.id}
        position={[b.x, b.baseY + BOX_HEIGHT / 2, b.z]}
        userData={{ boxId: b.id }}
        geometry={sharedGeometry}
        material={MACHINE_MATERIAL}
      />
    ) : (
      <mesh
        key={b.id}
        position={[b.x, b.baseY + BOX_HEIGHT / 2, b.z]}
        userData={{ boxId: b.id }}
        onClick={select(b.id)}
      >
        <boxGeometry args={[BOX_SIZE, BOX_HEIGHT, BOX_SIZE]} />
        <meshStandardMaterial color={b.baseY === 0 ? support.periwinkle : accent.butter} />
      </mesh>
    );

  return (
    <Page>
      <Controls>
        {/* Without a pose, as before R7: a pose given by the controls below is cleared first. */}
        <Button
          type="button"
          variant="primary"
          size="inline"
          onClick={() => {
            setEnterPose(null);
            setPlanView(true);
          }}
        >
          Enter plan view
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="inline"
          onClick={() => {
            setLeavePose(null);
            setPlanView(false);
          }}
        >
          Leave plan view
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="inline"
          onClick={() =>
            setMapStyle(s => (s === Config.mapboxStyleUrl ? alternateStyle : Config.mapboxStyleUrl))
          }
        >
          Reload map style
        </Button>
        {twoMaps && (
          <Button
            type="button"
            variant="secondary"
            size="inline"
            onClick={() => setFirstMapKey(k => k + 1)}
          >
            Remount map
          </Button>
        )}
        <BearingSlot>
          <Select
            ariaLabel="Bearing"
            value={bearing}
            onChange={turn}
            options={[
              { value: '0', label: '0°' },
              { value: '30', label: '30°' },
              { value: '90', label: '90°' },
            ]}
          />
        </BearingSlot>
        <Button
          type="button"
          variant="secondary"
          size="inline"
          onClick={() => setTranslucency(t => ({ ...t, post: t.post === false }))}
        >
          {translucency.post === false ? 'Post-processing: off' : 'Post-processing: on'}
        </Button>
        {/* R7's controls, after the rig's first ones so their Tab order stays as it was. */}
        <Button
          type="button"
          variant="secondary"
          size="inline"
          aria-pressed={free}
          onClick={() => setFree(f => !f)}
        >
          Free camera
        </Button>
        <Button type="button" variant="secondary" size="inline" onClick={leaveToPose}>
          Leave to pose
        </Button>
        <Button type="button" variant="secondary" size="inline" onClick={enterFromPose}>
          Enter from pose
        </Button>
        <Status aria-live="polite" data-testid="plan-map-lock-status">
          {announcement}
        </Status>
      </Controls>
      <MapRegion>
        <PlanMapHost
          key={firstMapKey}
          origin={origin}
          initialView={{ ...origin, zoom: 22, bearing: 0 }}
          planView={planView}
          free={free}
          leavePose={leavePose}
          enterPose={enterPose}
          mapStyle={mapStyle}
          onMapError={() => setAnnouncement('The map could not load')}
          postProcessing={translucency.post}
        >
          <ambientLight intensity={0.8} />
          <directionalLight position={[10, 30, 10]} intensity={0.8} />
          <PlanHeightGroup baseY={0}>{ground.map(draw)}</PlanHeightGroup>
          <PlanHeightGroup baseY={3.5}>{upper.map(draw)}</PlanHeightGroup>
          <TranslucentLayers layers={layers} state={translucency} />
          <EffectProps visible={translucency.extras} />
          <Driver
            boxes={boxes}
            onState={onState}
            tiltOnStart={!startLocked}
            translucency={translucencyApi}
            lock={lockApi}
          />
        </PlanMapHost>
      </MapRegion>
      {twoMaps && (
        <SecondMapRegion>
          <PlanMapHost origin={origin} initialView={{ ...origin, zoom: 21, bearing: 0 }}>
            <ambientLight intensity={0.8} />
            <directionalLight position={[10, 30, 10]} intensity={0.8} />
            <PlanHeightGroup baseY={0}>{ground.map(draw)}</PlanHeightGroup>
            <PlanHeightGroup baseY={3.5}>{upper.map(draw)}</PlanHeightGroup>
            <Driver boxes={boxes} onState={noState} tiltOnStart={false} name="__planMapLock2" />
          </PlanMapHost>
        </SecondMapRegion>
      )}
    </Page>
  );
};

export default PlanMapLockFixturePage;
